import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { PaginatedResponse } from "@/shared/http";
import type { SearchQueryBody } from "@/types";

import { ChatRecord } from "../ChatRecord";

export interface UseAgentSessionSearchOptions {
  /**
   * Caller's org id, sent to the backend as the ``X-Roboto-Org-Id``
   * header. Optional in the type only so callers can pass the unresolved
   * value during auth bootstrap; the hook stays disabled until this is
   * defined (see ``enabled`` below). The endpoint is org-scoped, and a
   * multi-org principal that omits the header gets a 400 ("provide a org
   * for single-org operation") from the backend auth layer.
   */
  orgId?: string;
  /**
   * Full POST body for ``v1/ai/chats/search``. ``SearchQueryBody`` bundles
   * the filter ``condition`` (typically built from the filter widget via
   * ``filterToCondition``) with ``limit``, ``after``, ``sort_by``, and
   * ``sort_direction``. The hook does no composition of its own — the
   * caller (Sessions table) assembles the whole body and the hook
   * forwards it. The backend enforces field and sort whitelists; sending
   * an unsupported field returns 400 and surfaces as ``isError``.
   */
  query: SearchQueryBody;
}

/**
 * Paginated search of agent sessions in the caller's org. Backs the
 * Agents > Sessions tab.
 *
 * Wraps ``chatService.searchAgentSessions`` in a tanstack-query
 * ``useQuery``. The query key includes ``orgId`` and ``query``, so any
 * change to filter/sort/pagination yields a distinct cache entry and
 * triggers a refetch on the next render. Results are considered fresh
 * for 30 seconds.
 *
 * Failure-mode posture: the hook stays disabled until ``orgId`` is
 * defined to avoid issuing a request the backend would reject with 400
 * for missing org context. Once enabled, the ``queryFn`` runs only when
 * ``orgId`` is set, which is why the cast to ``string`` at the call site
 * is safe.
 */
export function useAgentSessionSearch(
  options: UseAgentSessionSearchOptions,
): UseQueryResult<PaginatedResponse<ChatRecord>, Error> {
  const { chatService } = useDomainServices();

  return useQuery<PaginatedResponse<ChatRecord>, Error>({
    queryKey: ["agentSessionSearch", options.orgId, options.query],
    queryFn: ({ signal }) =>
      chatService.searchAgentSessions(options.query, {
        // ``orgId`` is guarded by ``enabled`` below — by the time the query
        // function runs, the value is defined.
        orgId: options.orgId as string,
        abortSignal: signal,
      }),
    enabled: options.orgId !== undefined,
    staleTime: 30_000,
  });
}
